import React, { useEffect, useMemo, useState } from 'react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IAuditoriaTributo, StatusAuditoria } from '../interfaces/IAuditoria';
import { formatCurrency, formatDate } from '../utils/formatters';

interface Props {
  auditoria?: IAuditoriaTributo;
}


const formatCurrencyCompact = (value: number) =>
  new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
    notation: 'compact',
    maximumFractionDigits: 1,
  }).format(value || 0);


const getCompensacaoSaldo = (auditoria: IAuditoriaTributo) =>
  auditoria.compensacaoSaldo ?? auditoria.confrontoRealizadoNoFechamento ?? 0;

const getPotencialCompensacao = (auditoria: IAuditoriaTributo) =>
  auditoria.potencialCompensacaoNaoAproveitado ?? auditoria.dinheiroDeixouDeCompensar ?? 0;

const getCompensacaoSaldoTrimestre = (trimestre: any) =>
  trimestre.compensacaoSaldo ?? trimestre.confrontoFechamento ?? 0;

const getPotencialCompensacaoTrimestre = (trimestre: any) =>
  trimestre.potencialCompensacaoNaoAproveitado ?? trimestre.deixouDeCompensar ?? 0;

const getIconeStatus = (status: StatusAuditoria) => {
  switch (status) {
    case 'ERRO':
      return 'bi-exclamation-circle-fill';
    case 'ALERTA':
      return 'bi-exclamation-triangle-fill';
    case 'AVISO':
      return 'bi-info-circle-fill';
    case 'CONCILIADO':
      return 'bi-check-circle-fill';
    default:
      return 'bi-question-circle-fill';
  }
};

const getLegendaStatus = (status: StatusAuditoria, mensagem?: string) => {
  switch (status) {
    case 'ERRO':
      return {
        titulo: 'ERRO',
        descricao:
          mensagem ||
          'Inconsistência crítica. O tributo possui valor a auditar, mas a provisão, compensação ou baixa não foi registrada conforme a regra de negócio.',
      };
    case 'ALERTA':
      return {
        titulo: 'ALERTA',
        descricao:
          mensagem ||
          'Existe crédito compensável disponível no ativo/retenções que não foi aproveitado na compensação do saldo.',
      };
    case 'AVISO':
      return {
        titulo: 'AVISO',
        descricao:
          mensagem ||
          'A compensação foi identificada parcialmente ou existe saldo residual que precisa ser revisado.',
      };
    case 'CONCILIADO':
      return {
        titulo: 'CONCILIADO',
        descricao:
          mensagem ||
          'Provisão, compensação do saldo e saldo residual estão aderentes à regra de negócio.',
      };
    default:
      return { titulo: status, descricao: mensagem || '-' };
  }
};


const AuditoriaDetalheTributo: React.FC<Props> = ({ auditoria }) => {
  const [paginaAtual, setPaginaAtual] = useState(1);
  const [porPagina, setPorPagina] = useState(50);
  const [paginaDigitada, setPaginaDigitada] = useState('1');

  useEffect(() => {
    setPaginaAtual(1);
    setPaginaDigitada('1');
  }, [auditoria?.tributo, auditoria?.lancamentos?.length]);

  const totaisTrimestrais = useMemo(() => {
    const trimestres = auditoria?.trimestres || [];
    return {
      faturamentoLiquido: trimestres.reduce((acc, x) => acc + (x.faturamentoLiquido || 0), 0),
      provisaoPassivo: trimestres.reduce((acc, x) => acc + (x.provisaoPassivo || 0), 0),
      compensacaoSaldo: trimestres.reduce((acc, x) => acc + getCompensacaoSaldoTrimestre(x), 0),
      potencialCompensacao: trimestres.reduce((acc, x) => acc + getPotencialCompensacaoTrimestre(x), 0),
      residualAberto: trimestres.reduce((acc, x) => acc + (x.residualAberto || 0), 0),
    };
  }, [auditoria?.trimestres]);

  const donutData = useMemo(() => {
    if (!auditoria) return [];
    const provisao = auditoria.saldoProvisaoRealPassivo || totaisTrimestrais.provisaoPassivo;
    if (provisao <= 0) return [];
    const compensado = auditoria.compensacaoSaldo ?? auditoria.confrontoRealizadoNoFechamento ?? 0;
    const pago = auditoria.valorPagoTotal ?? 0;
    const residual = auditoria.valorResidualAberto ?? 0;
    return [
      { nome: 'Compensado', valor: compensado },
      { nome: 'Pago/Baixado', valor: pago },
      { nome: 'Residual', valor: residual },
    ];
  }, [auditoria, totaisTrimestrais.provisaoPassivo]);

  const cotasChartData = useMemo(() => {
    return (auditoria?.sugestaoParcelamentoCotas || []).map((cota) => ({
      nome: `Cota ${cota.numeroCota}`,
      Principal: cota.valorPrincipal,
      Juros: cota.valorJurosCalculado,
    }));
  }, [auditoria?.sugestaoParcelamentoCotas]);

  if (!auditoria) return null;

  const lancamentos = auditoria.lancamentos || [];
  const totalPaginas = Math.max(Math.ceil(lancamentos.length / porPagina), 1);
  const paginaSegura = Math.min(Math.max(paginaAtual, 1), totalPaginas);

  const lancamentosPaginados = lancamentos.slice(
    (paginaSegura - 1) * porPagina,
    paginaSegura * porPagina
  );

  const irParaPaginaEspecifica = (pagina: number) => {
    const paginaValida = Number.isFinite(pagina) ? pagina : 1;
    const paginaAjustada = Math.min(Math.max(paginaValida, 1), totalPaginas);
    setPaginaAtual(paginaAjustada);
    setPaginaDigitada(String(paginaAjustada));
  };

  const irParaPagina = () => irParaPaginaEspecifica(Number(paginaDigitada));

  const compensacaoSaldo = getCompensacaoSaldo(auditoria);
  const potencialCompensacao = getPotencialCompensacao(auditoria);
  const residualAberto = auditoria.valorResidualAberto || 0;
  const donutProvisao = auditoria.saldoProvisaoRealPassivo || totaisTrimestrais.provisaoPassivo;
  const donutCompensado = donutData[0]?.valor ?? 0;
  const donutPago = donutData[1]?.valor ?? 0;
  const donutResidual = donutData[2]?.valor ?? 0;
  const percentualLiquidado =
    donutProvisao > 0
      ? Number(Math.min(((donutCompensado + donutPago) / donutProvisao) * 100, 100).toFixed(2))
      : 0;
  const tributoLiquidado = residualAberto <= 0 && auditoria.status === 'CONCILIADO';
  const legendaStatus = getLegendaStatus(
    auditoria.status,
    auditoria.legendaStatus || auditoria.mensagemDirecionamento
  );

  return (
    <>
      <div className={`auditoria-alert status-${auditoria.status}`} title={legendaStatus.descricao}>
        <i className={`bi ${getIconeStatus(auditoria.status)} auditoria-alert-icon`} />
        <div>
          <strong>{auditoria.tributo} — {legendaStatus.titulo}</strong>
          <div>{auditoria.mensagemDirecionamento}</div>
        </div>
      </div>

      <div className='auditoria-kpis'>
        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Provisão Real Passivo</div>
          <div className='auditoria-kpi-value'>{formatCurrency(auditoria.saldoProvisaoRealPassivo || totaisTrimestrais.provisaoPassivo)}</div>
          <div className='auditoria-kpi-help'>Soma das provisões identificadas em todos os trimestres do período.</div>
        </div>

        <div className='auditoria-kpi'>
          <div className='auditoria-kpi-label'>Compensação do Saldo</div>
          <div className='auditoria-kpi-value'>{formatCurrency(compensacaoSaldo || totaisTrimestrais.compensacaoSaldo)}</div>
          <div className='auditoria-kpi-help'>Valor compensado a partir das retenções/créditos referenciais do tributo.</div>
        </div>

        <div className={`auditoria-kpi ${potencialCompensacao === 0 ? 'auditoria-kpi-disabled' : 'auditoria-kpi-critico'}`}>
          <div className='auditoria-kpi-label'>Potencial de Compensação Não Aproveitado</div>
          <div className='auditoria-kpi-value'>{formatCurrency(potencialCompensacao)}</div>
          {potencialCompensacao === 0 ? (
            <span className='auditoria-kpi-selo'>Zero Desperdício</span>
          ) : (
            <div className='auditoria-kpi-help'>Saldo compensável que poderia ter reduzido o desembolso financeiro.</div>
          )}
        </div>

        <div className={`auditoria-kpi ${residualAberto <= 0 ? 'auditoria-kpi-disabled' : auditoria.status === 'ERRO' ? 'auditoria-kpi-critico' : ''}`}>
          <div className='auditoria-kpi-label'>
            Valor Residual em Aberto
            {residualAberto > 0 && (
              <span className={`auditoria-status status-${auditoria.status} ms-2`} style={{ fontSize: 10, padding: '2px 7px', verticalAlign: 'middle' }}>{auditoria.status}</span>
            )}
          </div>
          <div className='auditoria-kpi-value'>
            {residualAberto > 0 ? (
              <span className={auditoria.status === 'ERRO' ? 'auditoria-residual-erro' : ''}>{formatCurrency(residualAberto)}</span>
            ) : (
              <span style={{ color: '#126b3f' }}>Sem residual</span>
            )}
          </div>
          {residualAberto > 0 ? (
            <div className='auditoria-kpi-help' style={auditoria.status === 'ERRO' ? { color: '#9b1c1c' } : undefined}>
              {auditoria.status === 'ERRO'
                ? `Existe saldo de ${formatCurrency(residualAberto)} não liquidado. Verifique se há pagamentos em atraso, diferenças de compensação não regularizadas ou lançamentos de baixa pendentes.`
                : `Existe saldo de ${formatCurrency(residualAberto)} ainda dentro do prazo de vencimento. Verifique o pagamento ou compensação na seção de cotas.`}
            </div>
          ) : (
            <div className='auditoria-kpi-help'>Saldo remanescente após provisão, compensação e baixas/pagamentos.</div>
          )}
        </div>
      </div>

      <div className='auditoria-card'>
        <h3>{auditoria.tributo === 'PIS' || auditoria.tributo === 'COFINS' ? 'Análise Mensal' : 'Análise Trimestral'}</h3>
        <div className='table-responsive mt-4'>
          <table className='auditoria-table'>
            <thead>
              <tr>
                <th>{auditoria.tributo === 'PIS' || auditoria.tributo === 'COFINS' ? 'Competência' : 'Trimestre'}</th><th>Faturamento</th><th>Provisão</th><th>Compensação do Saldo</th><th>Potencial Não Aproveitado</th><th>Residual</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {auditoria.trimestres?.map((trimestre, index) => (
                <tr key={`${trimestre.periodo}-${index}`}>
                  <td>{trimestre.periodo}</td>
                  <td>{formatCurrency(trimestre.faturamentoLiquido)}</td>
                  <td>{formatCurrency(trimestre.provisaoPassivo)}</td>
                  <td>{formatCurrency(getCompensacaoSaldoTrimestre(trimestre))}</td>
                  <td>{formatCurrency(getPotencialCompensacaoTrimestre(trimestre))}</td>
                  <td>
                    {(trimestre.residualAberto ?? 0) > 0 ? (
                      <div className='d-flex align-items-center gap-1'>
                        <span className={trimestre.status === 'ERRO' ? 'auditoria-residual-erro' : ''}>{formatCurrency(trimestre.residualAberto)}</span>
                        <span className={`auditoria-status status-${trimestre.status}`} style={{ fontSize: 10, padding: '2px 7px' }}>{trimestre.status}</span>
                      </div>
                    ) : (
                      <span className='text-muted'>—</span>
                    )}
                  </td>
                  <td><span className={`auditoria-status status-${trimestre.status}`} title={trimestre.legendaStatus || trimestre.mensagem}>{trimestre.status}</span></td>
                </tr>
              ))}
              {(!auditoria.trimestres || auditoria.trimestres.length === 0) && (
                <tr><td colSpan={7} className='text-center text-muted py-6'>Nenhuma análise trimestral encontrada.</td></tr>
              )}
            </tbody>
            {auditoria.trimestres && auditoria.trimestres.length > 0 && (
              <tfoot>
                <tr className='auditoria-total-row'>
                  <td>TOTAL</td>
                  <td>{formatCurrency(totaisTrimestrais.faturamentoLiquido)}</td>
                  <td>{formatCurrency(totaisTrimestrais.provisaoPassivo)}</td>
                  <td>{formatCurrency(totaisTrimestrais.compensacaoSaldo)}</td>
                  <td>{formatCurrency(totaisTrimestrais.potencialCompensacao)}</td>
                  <td>
                    {totaisTrimestrais.residualAberto > 0 ? (
                      <div className='d-flex align-items-center gap-1'>
                        <span className={auditoria.status === 'ERRO' ? 'auditoria-residual-erro' : ''}>{formatCurrency(totaisTrimestrais.residualAberto)}</span>
                        <span className={`auditoria-status status-${auditoria.status}`} style={{ fontSize: 10, padding: '2px 7px' }}>{auditoria.status}</span>
                      </div>
                    ) : (
                      <span className='text-muted'>—</span>
                    )}
                  </td>
                  <td><span className={`auditoria-status status-${auditoria.status}`}>{auditoria.status}</span></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </div>

      {tributoLiquidado ? (
        <div className='auditoria-card auditoria-success-card'>
          <i className='bi bi-patch-check-fill' />
          <h3>Tributo 100% Liquidado</h3>
          <p>Sem saldos residuais ou necessidade de parcelamento para este período.</p>
        </div>
      ) : (
        <div className='auditoria-bottom-grid'>
          <div className='auditoria-card'>
            <h3>Composição da Liquidação do {auditoria.tributo}</h3>
            <div className='auditoria-donut-wrap'>
              <ResponsiveContainer width='100%' height={220}>
                <PieChart>
                  <Pie
                    data={donutData}
                    dataKey='valor'
                    nameKey='nome'
                    innerRadius={70}
                    outerRadius={95}
                    startAngle={90}
                    endAngle={-270}
                    stroke='none'
                  >
                    <Cell fill='#1bc5bd' />
                    <Cell fill='#005E96' />
                    <Cell fill='#f64e60' />
                  </Pie>
                  <Tooltip formatter={(value: number) => formatCurrency(value)} />
                </PieChart>
              </ResponsiveContainer>
              <div className='auditoria-donut-center'>
                <div className='auditoria-donut-center-value'>
                  {percentualLiquidado.toLocaleString('pt-BR', {
                    minimumFractionDigits: percentualLiquidado === 100 ? 0 : 2,
                    maximumFractionDigits: percentualLiquidado === 100 ? 0 : 2,
                  })}%
                </div>
              </div>
            </div>
            <div className='mt-4'>
              <div>Provisão Total: <strong>{formatCurrency(donutProvisao)}</strong></div>
              <div><span style={{ color: '#1bc5bd' }}>●</span> Compensado: <strong>{formatCurrency(donutCompensado)}</strong></div>
              <div><span style={{ color: '#005E96' }}>●</span> Pago/Baixado: <strong>{formatCurrency(donutPago)}</strong></div>
              {donutResidual > 0 && (
                <div><span style={{ color: '#f64e60' }}>●</span> Residual em Aberto: <strong style={{ color: '#f64e60' }}>{formatCurrency(donutResidual)}</strong></div>
              )}
            </div>
          </div>

          {(auditoria.tributo === 'IRPJ' || auditoria.tributo === 'CSLL') && (
          <div className='auditoria-card'>
            <h3>Projeção de Cotas e Auditoria de Juros</h3>
            {auditoria.sugestaoParcelamentoCotas?.length > 0 ? (
              <>
                <ResponsiveContainer width='100%' height={180}>
                  <BarChart data={cotasChartData} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray='3 3' vertical={false} />
                    <XAxis dataKey='nome' tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 11 }} width={56} tickFormatter={(v) => formatCurrencyCompact(Number(v))} />
                    <Tooltip formatter={(value: number) => formatCurrency(Number(value))} />
                    <Bar dataKey='Principal' stackId='cota' fill='#005E96' />
                    <Bar dataKey='Juros' stackId='cota' fill='#ffa800' radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>

                <table className='auditoria-table mt-4'>
                  <thead><tr><th>Nº Cota</th><th>Vencimento</th><th>Principal</th><th>Juros</th><th>Total</th><th>DRE</th></tr></thead>
                  <tbody>
                    {auditoria.sugestaoParcelamentoCotas.map((cota, index) => (
                      <tr key={`${cota.numeroCota}-${index}`}>
                        <td>{cota.numeroCota}</td><td>{formatDate(cota.dataVencimento)}</td><td>{formatCurrency(cota.valorPrincipal)}</td><td>{formatCurrency(cota.valorJurosCalculado)}</td><td><strong>{formatCurrency(cota.totalCota)}</strong></td>
                        <td>{cota.jurosValidadoDre ? <span className='auditoria-validado'>✅ Validado</span> : <span className='auditoria-inconsistente'>⚠️ Inconsistente</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            ) : <div className='text-muted mt-4'>Nenhuma cota projetada para este tributo.</div>}
            <div className='mt-5 text-muted'>Juros lançados na DRE: <strong>{formatCurrency(auditoria.jurosLancadosNaDRE)}</strong></div>
          </div>
          )}
        </div>
      )}

      <div className='auditoria-card mt-5'>
        <div className='d-flex justify-content-between align-items-center mb-5'>
          <h3 className='mb-0'>Lançamentos da Auditoria</h3>
          <div className='d-flex align-items-center gap-3 flex-wrap'>
            <span className='text-muted'>Pág. {paginaSegura} / {totalPaginas} - {lancamentos.length} lançamentos</span>
            <span className='text-muted'>Por página:</span>
            <select className='form-select form-select-sm' style={{ width: 90 }} value={porPagina} onChange={(e) => { setPorPagina(Number(e.target.value)); setPaginaAtual(1); setPaginaDigitada('1'); }}>
              <option value={50}>50</option><option value={100}>100</option><option value={200}>200</option>
            </select>
            <button className='btn btn-sm btn-light-primary' disabled={paginaSegura <= 1} onClick={() => irParaPaginaEspecifica(paginaSegura - 1)}>‹ Anterior</button>
            <button className='btn btn-sm btn-light-primary' disabled={paginaSegura >= totalPaginas} onClick={() => irParaPaginaEspecifica(paginaSegura + 1)}>Próxima ›</button>
            <span className='text-muted'>Ir para:</span>
            <input type='number' className='form-control form-control-sm' style={{ width: 90 }} min={1} max={totalPaginas} value={paginaDigitada} onChange={(e) => setPaginaDigitada(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter') irParaPagina(); }} />
            <button className='btn btn-sm btn-light-primary' onClick={irParaPagina}>Ir</button>
          </div>
        </div>

        <div className='table-responsive'>
          <table className='auditoria-table'>
            <thead><tr><th>Data</th><th>Trimestre</th><th>Classificação</th><th>Conta</th><th>Referencial</th><th>Histórico</th><th style={{ textAlign: 'right' }}>Valor</th></tr></thead>
            <tbody>
              {lancamentosPaginados.map((lancamento) => (
                <tr key={`${lancamento.id}-${lancamento.classificacao}-${lancamento.dataLancamento}`}>
                  <td>{formatDate(lancamento.dataLancamento)}</td><td>{lancamento.trimestre}</td><td>{lancamento.classificacao}</td>
                  <td><strong>{lancamento.contaContabil}</strong><div className='text-muted'>{lancamento.descricaoConta}</div></td>
                  <td>{lancamento.contaReferencial}</td><td>{lancamento.historico}</td><td style={{ textAlign: 'right', fontWeight: 800 }}>{formatCurrency(lancamento.valor)}</td>
                </tr>
              ))}
              {lancamentos.length === 0 && <tr><td colSpan={7} className='text-center text-muted py-10'>Nenhum lançamento encontrado para o tributo/período selecionado.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default AuditoriaDetalheTributo;
